// Pure, dependency-free navigation decision for the global router guard.
//
// This module MUST NOT import anything (no Vue, no store, no router) so the
// whole frontend RBAC decision can be reasoned about — and tested — in
// isolation. router.js owns the async/maintenance side effects; everything
// synchronous lives here.
//
// GroupId semantics behind the getters: 1 READ_ONLY, 2 CORPORATE_ADMIN,
// 3 DTAC_ADMIN, 4 CORPORATE_SUPER_ADMIN.

// Ordered, first-match-wins. This is a table on purpose: the original guard
// used an `else if` ladder, so the checks are NOT additive — the first role
// meta present on the matched route chain decides, and the others are never
// consulted.
// isMigrationUser is first because it is the only rule here that is not a
// GroupId test — it reads the config.yml allow-list (#47). hasMeta scans the
// WHOLE matched chain, so if a route (or any parent of it) ever carried a
// GroupId meta as well, the more specific allow-list must still be the rule
// that decides rather than being shadowed by a broader group check.
const ROLE_RULES = [
  { meta: "isMigrationUser", getter: "Auth/isMigrationUser" },
  { meta: "isDtacAdmin", getter: "Auth/isDtacAdmin" },
  { meta: "isCorporateAdmin", getter: "Auth/isCorporateAdmin" },
  { meta: "isReadOnly", getter: "Auth/isReadOnly" }
];

// The maintenance screens must stay reachable no matter what: they are how a
// locked-out user gets back in.
export function isMaintenancePath(path) {
  const p = typeof path === "string" ? path : "";
  return p === "/maintenance" || p.startsWith("/login/maintenance/");
}

// True when any matched record carries a truthy `meta[key]`.
function hasMeta(to, key) {
  const matched = to?.matched || [];
  return matched.some(record => Boolean(record?.meta?.[key]));
}

function findRoleRule(to) {
  return ROLE_RULES.find(rule => hasMeta(to, rule.meta));
}

// Returns the argument to hand to next(): undefined means "allow through".
export function resolveNavigation(to, getters) {
  if (isMaintenancePath(to?.path)) {
    return undefined;
  }
  if (!hasMeta(to, "requiresAuth")) {
    // `guest` meta and no meta at all both land here — allow through.
    return undefined;
  }
  if (!getters["Auth/isAuthenticate"]) {
    // vue-router ignores `params` on a path-based location; kept verbatim
    // because changing it is out of scope.
    return { path: "/login", params: { nextUrl: to.fullPath } };
  }
  const rule = findRoleRule(to);
  if (rule && !getters[rule.getter]) {
    return { path: "/portal" };
  }
  return undefined;
}
